/**
 * URL as the app's state, hand-rolled.
 *
 * No router library, because there is nothing here a library would earn: five
 * screens, no nesting, no route-level data loading (react-query owns that). What is
 * needed is smaller than the dependency — read the URL on load, write it on
 * navigation, listen for Back.
 *
 * The whole map lives in `parse`/`build`, which are pure and inverse. That is the
 * property worth protecting: any state reachable by clicking must survive a
 * refresh, and any URL you can type must produce that state.
 */

import { type BudgetMonth, Month } from '@ynab/core'
import { useCallback, useEffect, useState } from 'react'
import { SCREENS, type Screen } from '../screens.ts'

export interface Route {
  screen: Screen
  /** Looking back's end month. Null means "the current month", so a bookmark stays current. */
  month: BudgetMonth | null
  /** The forecast's horizon in days. Null is the default. */
  days: number | null
}

export const HORIZONS = [30, 90, 180, 365] as const
const DEFAULT_DAYS = 90

export function parse(url: URL): Route {
  const [head] = url.pathname.split('/').filter(Boolean)
  const match = SCREENS.find((screen) => screen.path === `/${head ?? ''}`)
  const monthParam = url.searchParams.get('month')
  const daysParam = Number(url.searchParams.get('days'))
  return {
    screen: match?.id ?? 'ahead',
    // A hand-edited value that is not real is ignored rather than thrown over: a bad
    // URL should land you somewhere sensible, not on an error screen.
    month: monthParam && Month.isBudgetMonth(monthParam) ? Month.budgetMonth(monthParam) : null,
    days: (HORIZONS as readonly number[]).includes(daysParam) ? daysParam : null,
  }
}

export function build(route: Route, now: BudgetMonth): string {
  const path = SCREENS.find((screen) => screen.id === route.screen)?.path ?? '/'
  // Only what differs from the default gets a query param, so a shared link does not
  // freeze "now" into it.
  const query = new URLSearchParams()
  if (route.screen === 'back' && route.month && route.month !== now) query.set('month', route.month)
  if (route.screen === 'ahead' && route.days && route.days !== DEFAULT_DAYS) {
    query.set('days', String(route.days))
  }
  const search = query.toString()
  return search ? `${path}?${search}` : path
}

export function daysOf(route: Route): number {
  return route.days ?? DEFAULT_DAYS
}

/**
 * The current route, and a way to change it.
 *
 * `navigate` takes a patch rather than a whole route so a call site can say what it
 * means — `navigate({ month })` — without restating everything else.
 */
export function useRoute(): [Route, (patch: Partial<Route>) => void] {
  const [route, setRoute] = useState<Route>(() => parse(new URL(window.location.href)))

  useEffect(() => {
    // Back and forward. Without this the address bar moves and the app does not.
    const onPop = () => setRoute(parse(new URL(window.location.href)))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback((patch: Partial<Route>) => {
    setRoute((current) => {
      const next: Route = { ...current, ...patch }
      const url = build(next, Month.currentMonth())
      if (url !== `${window.location.pathname}${window.location.search}`) {
        window.history.pushState(null, '', url)
      }
      return next
    })
  }, [])

  return [route, navigate]
}
